import { Component, inject, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatBadgeModule } from "@angular/material/badge";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatListModule } from "@angular/material/list";
import { MatDividerModule } from "@angular/material/divider";
import { MatChipsModule } from "@angular/material/chips";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { DocumentNotificationService, DocumentNotification } from "./document-notification.service";
import { PushNotificationService } from "./push-notification.service";

@Component({
    selector: "eforsyning-notification-center",
    standalone: true,
    imports: [CommonModule, MatBadgeModule, MatButtonModule, MatIconModule, MatMenuModule, MatListModule, MatDividerModule, MatChipsModule],
    templateUrl: "./notification-center.component.html",
    styleUrls: ["./notification-center.component.scss"]
})
export class NotificationCenterComponent implements OnInit, OnDestroy {
    private readonly documentNotificationService = inject(DocumentNotificationService);
    private readonly pushNotificationService = inject(PushNotificationService);

    private readonly destroy$ = new Subject<void>();

    public notifications: DocumentNotification[] = [];
    public unreadCount = 0;

    ngOnInit(): void {
        this.loadNotifications();
        this.subscribeToNotifications();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public onNotificationClick(notification: DocumentNotification): void {
        if (!notification.isRead) {
            this.documentNotificationService.markAsRead(notification.id).subscribe();
        }

        if (notification.documentUrl) {
            window.open(notification.documentUrl, "_blank");
        }
    }

    public deleteNotification(notificationId: string, event: Event): void {
        event.stopPropagation();
        this.documentNotificationService.deleteNotification(notificationId).subscribe();
    }

    public markAllAsRead(): void {
        this.documentNotificationService.markAllAsRead().subscribe();
    }

    public getNotificationIcon(documentType: string): string {
        switch (documentType.toLowerCase()) {
            case "faktura":
                return "receipt";
            case "årsopgørelse":
                return "description";
            case "meddelelse":
                return "campaign";
            case "regning":
                return "payment";
            default:
                return "insert_drive_file";
        }
    }

    private loadNotifications(): void {
        this.documentNotificationService
            .getNotifications()
            .pipe(takeUntil(this.destroy$))
            .subscribe((notifications) => {
                this.notifications = notifications;
                this.updateUnreadCount();
            });
    }

    private subscribeToNotifications(): void {
        this.documentNotificationService.notifications$.pipe(takeUntil(this.destroy$)).subscribe((notifications) => {
            this.notifications = notifications;
            this.updateUnreadCount();
        });
    }

    private updateUnreadCount(): void {
        this.unreadCount = this.notifications.filter((n) => !n.isRead).length;
    }
}
